/**
 * cloudRefresh — the portal Microsoft OAuth session refresh loop (card 86bbk70nq).
 *
 * The hosted CloudNVR viewer's data-plane cloud JWT lives only 1 hour so a
 * Microsoft revocation bites quickly. Before it expires the viewer POSTs its RS256
 * refresh token to /ve/session/refresh, which re-reads the JSUser (fDisabled) and
 * re-mints a fresh 1-hour cloud JWT — UNLESS the hourly server-side worker has
 * revoked the identity (or an admin disabled the user), in which case the refresh
 * 401s and the viewer logs out. This is what makes revocation actually cut a
 * browser user off. Password cloud login has no refresh token and is untouched.
 */

import { veCall } from './veCall';

// Refresh comfortably before the 1-hour cloud JWT expires.
export const CLOUD_REFRESH_INTERVAL_MS = 50 * 60 * 1000; // 50 min
// After a TRANSIENT failure, retry soon rather than waiting a full interval: a
// single blip at the 50-min tick would otherwise leave the JWT to expire (60 min)
// with the next attempt only at 100 min, so the data plane 401s for ~40 min before
// self-healing. A short backoff keeps several attempts inside the TTL window.
export const CLOUD_REFRESH_RETRY_MS = 2 * 60 * 1000; // 2 min

/**
 * POST /ve/session/refresh with the portal refresh token. Resolves with the
 * response body ({ token, refreshToken, ... }). On a 401 (revoked/disabled/expired)
 * the thrown error carries `.expired = true` so the caller routes to login.
 * @param {string} refreshToken
 * @returns {Promise<Object>}
 */
export const refreshCloudSession = async (refreshToken) => {
  try {
    const res = await veCall('session/refresh', { method: 'post', data: { refreshToken } });
    return res?.data || {};
  } catch (e) {
    const status = e && e.response && e.response.status;
    if (status === 401) {
      const err = new Error('session_expired');
      err.expired = true;
      throw err;
    }
    throw e; // transient (5xx / network / timeout): keep the current token, retry next tick
  }
};

/**
 * One refresh attempt. On success calls onToken(freshCloudJwt); on a 401 calls
 * onExpired(). A transient error is swallowed (keep the current token). Returns a
 * small result object for callers/tests.
 * @param {Object} opts
 * @param {function():string|null} opts.getRefreshToken
 * @param {function(string):void} opts.onToken
 * @param {function(Object):(boolean|Promise<boolean>)} [opts.onSession] - the whole refreshed payload
 *   (preferred over onToken when it carries devices); resolves true when access changed
 * @param {function():void} opts.onExpired
 */
export const runOneRefresh = async ({ getRefreshToken, onToken, onSession, onExpired } = {}) => {
  const rt = typeof getRefreshToken === 'function' ? getRefreshToken() : null;
  if (!rt) return { skipped: true };
  try {
    const data = await refreshCloudSession(rt);
    // cloudapi returns the whole login payload here (#86bc6dkfa): onSession adopts the token AND any
    // access change, and says whether access changed. An older cloudapi answers with the token alone
    // (no devices): that only rolls the token, never ends a session for want of devices (review L2).
    if (data && data.token) {
      if (typeof onSession === 'function' && Array.isArray(data.devices)) {
        return { ok: true, changed: Boolean(await onSession(data)) };
      }
      if (typeof onToken === 'function') onToken(data.token);
    }
    return { ok: true, changed: false };
  } catch (e) {
    if (e && e.expired) {
      if (typeof onExpired === 'function') onExpired();
      return { expired: true };
    }
    return { transient: true }; // keep the current token, try again next tick
  }
};

/**
 * Start a refresh timer. Returns a stop function. On a 401 it stops itself before
 * calling the caller's onExpired.
 * @param {Object} opts
 * @param {function():string|null} opts.getRefreshToken
 * @param {function(string):void} opts.onToken   - called with the fresh cloud JWT
 * @param {function():void} opts.onExpired       - called on a 401 (then the timer stops)
 */
export const startCloudRefreshTimer = ({
  getRefreshToken,
  onToken,
  onSession,
  onExpired,
  intervalMs = CLOUD_REFRESH_INTERVAL_MS,
  retryMs = CLOUD_REFRESH_RETRY_MS,
  setTimer = setTimeout,
  clearTimer = clearTimeout,
} = {}) => {
  let handle = null;
  let stopped = false;
  const stop = () => { stopped = true; try { clearTimer(handle); } catch (e) { /* ignore */ } };
  const schedule = (ms) => { if (!stopped) handle = setTimer(tick, ms); };
  // Self-rescheduling so the delay adapts: a transient blip retries soon, a
  // success waits the full interval, a 401 stops the loop (onExpired).
  const tick = async () => {
    const r = await runOneRefresh({
      getRefreshToken,
      onToken,
      onSession,
      onExpired: () => { stop(); if (typeof onExpired === 'function') onExpired(); },
    });
    if (stopped) return;
    schedule(r && r.transient ? retryMs : intervalMs);
  };
  schedule(intervalMs);
  return stop;
};

// Module singleton so login starts exactly one loop and logout/expiry stops it.
let _stop = null;

/** Start (or restart) the single cloud refresh loop. */
export const startCloudSessionRefresh = (opts) => {
  stopCloudSessionRefresh();
  _stop = startCloudRefreshTimer(opts);
};

/** Stop the cloud refresh loop, if running. */
export const stopCloudSessionRefresh = () => {
  if (_stop) { _stop(); _stop = null; }
};

/** Testing: whether the singleton loop is currently running. */
export const _isCloudRefreshRunning = () => _stop != null;
